import { inject } from '@angular/core';
import { map, tap } from 'rxjs';
import { patchState, signalStore, withMethods } from '@ngrx/signals';
import { removeEntity, withEntities } from '@ngrx/signals/entities';
import { withCursorPagination } from '@dgacontrol-frontend/shared-util-cursor-pagination';

import { BillingQuoteRequestService } from './generated/billing-quote-request/billing-quote-request.service';
import type {
  BillingV1QuoteRequestsListParams,
  QuoteCreateRequestV1Request,
  QuoteRequestV1,
} from './generated/types';

/**
 * TARG-4 pure: injects `BillingQuoteRequestService` directly, no facade. The admin inbox of
 * the landing's quote requests (M-bill-19): cursor list for `dt-quote-requests`
 * (component-driven `load`, `?attended=false` is the inbox), one request for «Ver solicitud»,
 * the trash, and «Crear cotización». A request has no PATCH: it is the record.
 *
 * The landing's POST is not here: it is a one-shot of `www` with no state to share.
 */
export const QuoteRequestsStore = signalStore(
  withEntities<QuoteRequestV1>(),
  withCursorPagination<QuoteRequestV1, BillingV1QuoteRequestsListParams>(() => {
    const api = inject(BillingQuoteRequestService);
    return (query, cursor) => api.billingV1QuoteRequestsList({ ...query, cursor: cursor ?? undefined });
  }),
  withMethods((store, api = inject(BillingQuoteRequestService)) => ({
    getQuoteRequest: (identity: string) =>
      api.billingV1QuoteRequestsRetrieve({ identity }).pipe(map((res) => res.quote_request)),
    deleteQuoteRequest(identity: string, id: number) {
      return api.billingV1QuoteRequestsDestroy({ identity }).pipe(
        tap(() => {
          patchState(store, removeEntity(id));
        }),
      );
    },
    /** The body is the one of POST /v1/billing/quotes/, filled in from the request; the
     *  quote gets a copy of its works. 409 `quote-request-attended` while its quote exists. */
    createQuote: (identity: string, body: QuoteCreateRequestV1Request) =>
      api.billingV1QuoteRequestQuotesCreate({ identity }, body).pipe(map((res) => res.quote)),
  })),
);
